import type { Component, VoidComponent } from "solid-js";
import {
  createMemo,
  createSignal,
  createUniqueId,
  getOwner,
  Match,
  runWithOwner,
  Show,
  Switch,
  untrack,
} from "solid-js";
import type { RouteSectionProps } from "@solidjs/router";
import withPagePermissionCheck from "../../../utils/withPagePermissionCheck";
import PermissionService from "../../../services/PermissionService";
import { useTranslation } from "../../../state/i18n";
import { useModalManager } from "../../../components/modal/ModalManager";
import ApiService from "../../../services/ApiService";
import EmptyState from "../../../components/emptyState/EmptyState";
import ContentSection from "../../formEditor/ContentSection";
import { IconButton, TextAndIconButton, TextButton } from "../../../components/button/Button";
import Pill from "../../../components/pill/Pill";
import { DestructiveModal } from "../../../components/modal/Modal";
import SeparatorLine from "../../../components/SeparatorLine";
import InputModal from "../../../components/modal/InputModal";
import { CompactTextInput, TextInput } from "../../../components/inputs/TextInput";
import { ErrorMessage, Header } from "../../../components/inputs/Input";
import { noop } from "../../../utils/noop";
import InlineMessage from "../../../components/inlineMessage/InlineMessage";
import { Form } from "../../../components/form/Form";
import CopyValueButton from "./CopyValueButton";
import GenericFailureModal from "../../GenericFailureModal";
import { isEmpty } from "lodash-es";
import withPageProductFeatureCheck from "../../../utils/withPageProductFeatureCheck";
import type { EmailConfiguration } from "../../../types/EmailConfiguration";
import { emailConfigurationSchema } from "../../../types/EmailConfiguration";
import { Icon } from "../../../components/icon/Icon";
import { createForm } from "../../../components/form/createForm";
import SettingsPageHeader from "./components/SettingsPageHeader.tsx";
import Text from "../../../components/text/Text";

const EmailConfigurationPage: Component<RouteSectionProps<never>> = () => {
  const owner = getOwner();
  const { t } = useTranslation();
  const { open, replace } = useModalManager();
  const [isEditing, setIsEditing] = createSignal(false);
  const [isVerifying, setIsVerifying] = createSignal(false);
  const [showVerificationFailedMessage, setShowVerificationFailedMessage] = createSignal(false);
  const createEmailConfigurationMutation = ApiService.createEmailConfiguration();
  const emailConfigurationQuery = ApiService.getEmailConfiguration();
  const is404 = createMemo(() => emailConfigurationQuery.isError && emailConfigurationQuery.error?.status === 404);

  const openCreateModal = () => {
    open({
      content: () => (
        <InputModal
          variant="default"
          size="large"
          title={t(($) => $.SETTINGS_EMAIL_CONFIGURATION_CONFIGURE_MODAL_TITLE)}
          titleIcon="mail"
          confirmLabel={t(($) => $.SETTINGS_EMAIL_CONFIGURATION_ACTIONS_CONFIRM)}
          cancelLabel={t(($) => $.SETTINGS_EMAIL_CONFIGURATION_ACTIONS_CANCEL)}
          onConfirm={({ domain, fromName, fromEmail }) =>
            new Promise((resolve, reject) => {
              createEmailConfigurationMutation.mutate(
                {
                  domain,
                  fromName,
                  fromEmail: `${fromEmail}@${domain}`,
                },
                {
                  onSuccess: () => {
                    resolve();
                  },
                  onError: (err) => {
                    replace({
                      content: () => <GenericFailureModal error={err} />,
                    });
                    reject(err);
                  },
                },
              );
            })
          }
          schema={emailConfigurationSchema}
        >
          {({ value, updateValue, error }) => {
            const fromEmailInputId = createUniqueId();

            return (
              <div class="flex flex-col gap-4">
                <TextInput
                  label={t(($) => $.SETTINGS_EMAIL_CONFIGURATION_INPUT_DOMAIN)}
                  required
                  value={value("domain")}
                  placeholder={t(($) => $.SETTINGS_EMAIL_CONFIGURATION_INPUT_DOMAIN_PLACEHOLDER)}
                  onBlur={(e) => {
                    updateValue("domain", e.target.value);
                  }}
                  onInput={(e) => {
                    updateValue("domain", e.target.value);
                  }}
                  errorMessage={error("domain")}
                />

                <TextInput
                  label={t(($) => $.SETTINGS_EMAIL_CONFIGURATION_INPUT_FROM_NAME)}
                  required
                  value={value("fromName")}
                  placeholder={t(($) => $.SETTINGS_EMAIL_CONFIGURATION_INPUT_FROM_NAME_PLACEHOLDER)}
                  onBlur={(e) => {
                    updateValue("fromName", e.target.value);
                  }}
                  onInput={(e) => {
                    updateValue("fromName", e.target.value);
                  }}
                  errorMessage={error("fromName")}
                />

                <div class="flex flex-col gap-1">
                  <Header
                    id={fromEmailInputId}
                    label={t(($) => $.SETTINGS_EMAIL_CONFIGURATION_INPUT_FROM_EMAIL)}
                    required
                  />
                  <div class="flex gap-1">
                    <CompactTextInput
                      id={fromEmailInputId}
                      value={value("fromEmail")}
                      placeholder={t(($) => $.SETTINGS_EMAIL_CONFIGURATION_INPUT_FROM_EMAIL_PLACEHOLDER)}
                      onBlur={(e) => {
                        updateValue("fromEmail", e.target.value);
                      }}
                      onInput={(e) => {
                        updateValue("fromEmail", e.target.value);
                      }}
                      errorMessage={error("fromEmail")}
                    />
                    <CompactTextInput
                      id={createUniqueId()}
                      ariaLabel={t(($) => $.SETTINGS_EMAIL_CONFIGURATION_INPUT_FROM_EMAIL_DOMAIN_ARIA_LABEL)}
                      disabled
                      value={`@${isEmpty(value("domain")) ? t(($) => $.SETTINGS_EMAIL_CONFIGURATION_INPUT_DOMAIN_PLACEHOLDER) : value("domain")}`}
                      onBlur={noop}
                    />
                  </div>
                  <ErrorMessage errorMessage={error("fromEmail")} />
                </div>
              </div>
            );
          }}
        </InputModal>
      ),
    });
  };

  return (
    <div class="flex h-full flex-col">
      <SettingsPageHeader
        title={t(($) => $.SETTINGS_SECTION_CONNECTIONS_LABEL)}
        description={t(($) => $.SETTINGS_ITEM_EMAIL_CONFIGURATION_LABEL)}
      />

      <div class="relative h-full min-h-0" aria-busy={emailConfigurationQuery.isFetching}>
        <Switch>
          <Match when={emailConfigurationQuery.isLoading}>
            <div class="absolute flex size-full flex-col items-center justify-center bg-surface-default opacity-75">
              <Icon name="spinner" size="2xl" type="solid" class="animate-spin icon-secondary" />
              <span class="text-medium-regular text-primary">
                {t(($) => $.SETTINGS_EMAIL_CONFIGURATION_LOADING_TITLE)}
              </span>
            </div>
          </Match>
          <Match when={is404()}>
            <div class="pt-8">
              <NoEmailConfiguration actionOnClick={() => openCreateModal()} />
            </div>
          </Match>
          <Match when={emailConfigurationQuery.data}>
            {(emailConfiguration) => {
              const isVerified = createMemo(() => emailConfiguration().isVerified);

              return (
                <div class="h-full max-w-400 overflow-y-auto pt-8 pb-6">
                  <ContentSection>
                    <div class="flex flex-col gap-6">
                      <div class="flex items-center gap-1">
                        <div class="flex min-w-0 flex-col gap-1">
                          <div class="flex items-center gap-2">
                            <Text
                              as="p"
                              class="text-header-lg text-primary"
                              text={emailConfiguration().domain}
                              truncate
                            />

                            <Show
                              when={isVerified()}
                              fallback={
                                <Pill
                                  variant="warning"
                                  label={t(($) => $.SETTINGS_EMAIL_CONFIGURATION_STATUS_UNVERIFIED)}
                                />
                              }
                            >
                              <Pill
                                variant="success"
                                label={t(($) => $.SETTINGS_EMAIL_CONFIGURATION_STATUS_VERIFIED)}
                              />
                            </Show>
                          </div>
                          <p class="text-base-regular text-secondary">
                            {t(($) => $.SETTINGS_EMAIL_CONFIGURATION_STATUS_DESCRIPTION)}
                          </p>
                        </div>
                        <div class="ml-auto flex gap-1">
                          <Show when={!isEditing()}>
                            <Show when={!isVerified()}>
                              <TextButton
                                variant="primary"
                                size="large"
                                loading={isVerifying()}
                                text={t(($) => $.SETTINGS_EMAIL_CONFIGURATION_ACTIONS_VERIFY_LABEL)}
                                onClick={() => {
                                  runWithOwner(owner, () => {
                                    setIsVerifying(true);
                                    setShowVerificationFailedMessage(false);
                                    ApiService.verifyEmailConfiguration().mutate(undefined, {
                                      onSuccess: (data) => {
                                        setShowVerificationFailedMessage(!data.isVerified);
                                      },
                                      onError: (err) => {
                                        open({
                                          content: () => <GenericFailureModal error={err} />,
                                        });
                                      },
                                      onSettled: () => {
                                        setIsVerifying(false);
                                      },
                                    });
                                  });
                                }}
                              />
                            </Show>
                            <Switch>
                              <Match when={isVerified()}>
                                <TextAndIconButton
                                  variant="secondary"
                                  size="large"
                                  icon="pencil"
                                  text={t(($) => $.SETTINGS_EMAIL_CONFIGURATION_ACTIONS_EDIT_LABEL)}
                                  onClick={() => setIsEditing(true)}
                                />
                              </Match>
                              <Match when={!isVerified()}>
                                <IconButton
                                  variant="secondary"
                                  size="large"
                                  icon="pencil"
                                  aria-label={t(($) => $.SETTINGS_EMAIL_CONFIGURATION_ACTIONS_EDIT_LABEL)}
                                  onClick={() => setIsEditing(true)}
                                />
                              </Match>
                            </Switch>
                          </Show>
                          <IconButton
                            variant="secondary"
                            size="large"
                            icon="trash"
                            aria-label={t(($) => $.SETTINGS_EMAIL_CONFIGURATION_ACTIONS_DELETE_LABEL)}
                            onClick={() => {
                              // Deletion triggers a SolidJS update, which can cause this reference to be stale and
                              // cause a crash. Since it's just used in a modal, we can safely untrack it.
                              const isShared = untrack(() => emailConfiguration().shared);

                              open({
                                content: () => (
                                  <DestructiveModal
                                    confirmLabel={t(($) => $.SETTINGS_EMAIL_CONFIGURATION_DELETE_MODAL_ACTIONS_DELETE)}
                                    cancelLabel={t(($) => $.SETTINGS_EMAIL_CONFIGURATION_DELETE_MODAL_ACTIONS_CANCEL)}
                                    title={t(($) => $.SETTINGS_EMAIL_CONFIGURATION_DELETE_MODAL_TITLE)}
                                    description={t(($) => $.SETTINGS_EMAIL_CONFIGURATION_DELETE_MODAL_DESCRIPTION)}
                                    inlineMessage={
                                      isShared ? t(($) => $.SETTINGS_EMAIL_CONFIGURATION_SHARED_WARNING) : undefined
                                    }
                                    onConfirm={() =>
                                      new Promise<void>((resolve, reject) =>
                                        ApiService.deleteEmailConfiguration().mutate(undefined, {
                                          onSuccess: () => {
                                            resolve();
                                          },
                                          onError: (err) => {
                                            replace({
                                              content: () => <GenericFailureModal error={err} />,
                                            });
                                            reject(err);
                                          },
                                        }),
                                      )
                                    }
                                  />
                                ),
                              });
                            }}
                          />
                        </div>
                      </div>

                      <Show when={showVerificationFailedMessage()}>
                        <InlineMessage
                          variant="warning"
                          label={t(($) => $.SETTINGS_EMAIL_CONFIGURATION_COULD_NOT_VERIFY_WARNING)}
                        />
                      </Show>

                      <Show
                        when={isEditing()}
                        fallback={
                          <>
                            <div>
                              <p class="text-base-semibold text-secondary">
                                {t(($) => $.SETTINGS_EMAIL_CONFIGURATION_FROM_NAME)}
                              </p>
                              <Text
                                as="p"
                                class="text-base-regular text-primary"
                                text={emailConfiguration().fromName}
                                truncate
                              />
                            </div>

                            <div>
                              <p class="text-base-semibold text-secondary">
                                {t(($) => $.SETTINGS_EMAIL_CONFIGURATION_FROM_EMAIL)}
                              </p>
                              <Text
                                as="p"
                                class="text-base-regular text-primary"
                                text={emailConfiguration().fromEmail}
                                truncate
                              />
                            </div>
                          </>
                        }
                      >
                        <EditEmailConfigurationForm
                          emailConfiguration={emailConfiguration()}
                          onEditChange={setIsEditing}
                        />
                      </Show>

                      <SeparatorLine />

                      <div>
                        <span class="text-header-lg text-primary">
                          {t(($) => $.SETTINGS_EMAIL_CONFIGURATION_VERIFICATION_TITLE)}
                        </span>
                      </div>

                      <DNSRecord {...emailConfiguration().records.sending} />

                      <SeparatorLine />

                      <DNSRecord {...emailConfiguration().records.bounce} />
                    </div>
                  </ContentSection>
                </div>
              );
            }}
          </Match>
        </Switch>
      </div>
    </div>
  );
};

type EditEmailConfigurationFormProps = {
  emailConfiguration: EmailConfiguration;
  onEditChange: (value: boolean) => void;
};
const EditEmailConfigurationForm: VoidComponent<EditEmailConfigurationFormProps> = (props) => {
  const { t } = useTranslation();
  const { open } = useModalManager();

  const form = createForm(
    untrack(() => emailConfigurationSchema),
    async ({ domain, fromName, fromEmail }) =>
      new Promise((resolve, reject) => {
        ApiService.updateEmailConfiguration().mutate(
          { fromName, fromEmail: `${fromEmail}@${domain}` },
          {
            onSuccess: () => {
              props.onEditChange(false);
              resolve();
            },
            onError: (err) => {
              open({
                content: () => <GenericFailureModal error={err} />,
              });
              reject(err);
            },
          },
        );
      }),
    untrack(() => ({
      domain: props.emailConfiguration.domain,
      fromName: props.emailConfiguration.fromName,
      fromEmail: props.emailConfiguration.fromEmail.split("@")[0],
    })),
  );
  const fromEmailInputId = createUniqueId();

  return (
    <Form form={form}>
      {({ value, updateValue, error }) => (
        <div class="flex flex-col gap-6">
          <TextInput
            label={t(($) => $.SETTINGS_EMAIL_CONFIGURATION_INPUT_FROM_NAME)}
            required
            value={value("fromName")}
            placeholder={t(($) => $.SETTINGS_EMAIL_CONFIGURATION_INPUT_FROM_NAME_PLACEHOLDER)}
            onBlur={(e) => {
              updateValue("fromName", e.target.value);
            }}
            onInput={(e) => {
              updateValue("fromName", e.target.value);
            }}
            errorMessage={error("fromName")}
          />

          <div class="flex flex-col gap-1">
            <Header id={fromEmailInputId} label={t(($) => $.SETTINGS_EMAIL_CONFIGURATION_INPUT_FROM_EMAIL)} required />
            <div class="flex gap-1">
              <CompactTextInput
                id={fromEmailInputId}
                value={value("fromEmail")}
                placeholder={t(($) => $.SETTINGS_EMAIL_CONFIGURATION_INPUT_FROM_EMAIL)}
                onBlur={(e) => {
                  updateValue("fromEmail", e.target.value);
                }}
                onInput={(e) => {
                  updateValue("fromEmail", e.target.value);
                }}
                errorMessage={error("fromEmail")}
              />
              <CompactTextInput
                id={createUniqueId()}
                ariaLabel={t(($) => $.SETTINGS_EMAIL_CONFIGURATION_INPUT_FROM_EMAIL_DOMAIN_ARIA_LABEL)}
                disabled
                value={`@${value("domain")}`}
                onBlur={noop}
              />
            </div>
            <ErrorMessage errorMessage={error("fromEmail")} />
          </div>

          <Show when={props.emailConfiguration.shared}>
            <InlineMessage variant="warning" label={t(($) => $.SETTINGS_EMAIL_CONFIGURATION_SHARED_WARNING)} />
          </Show>

          <div class="flex justify-end gap-2">
            <TextButton
              text={t(($) => $.SETTINGS_EMAIL_CONFIGURATION_ACTIONS_CANCEL)}
              disabled={form.isSubmitting()}
              variant="secondary"
              size="large"
              onClick={() => props.onEditChange(false)}
            />
            <TextButton
              text={t(($) => $.SETTINGS_EMAIL_CONFIGURATION_ACTIONS_SAVE)}
              disabled={form.submitDisabled()}
              loading={form.isSubmitting()}
              size="large"
              form={form.id}
              type="submit"
            />
          </div>
        </div>
      )}
    </Form>
  );
};

const DNSRecord = (props: { type: string; hostname: string; value: string }) => {
  const { t } = useTranslation();
  const nameInputId = createUniqueId();
  const valueInputId = createUniqueId();

  return (
    <div class="flex flex-col gap-4">
      <div class="flex flex-col gap-1">
        <p class="text-base-semibold text-secondary">{t(($) => $.SETTINGS_EMAIL_CONFIGURATION_DNS_RECORD_TYPE)}</p>
        <p class="text-base-regular text-primary">{props.type}</p>
      </div>

      <div class="flex flex-col gap-1">
        <Header id={nameInputId} label={t(($) => $.SETTINGS_EMAIL_CONFIGURATION_DNS_RECORD_NAME)} />
        <div class="flex gap-1">
          <CompactTextInput id={nameInputId} disabled onBlur={noop} value={props.hostname} />
          <CopyValueButton value={props.hostname} />
        </div>
      </div>

      <div class="flex flex-col gap-1">
        <Header id={valueInputId} label={t(($) => $.SETTINGS_EMAIL_CONFIGURATION_DNS_RECORD_VALUE)} />
        <div class="flex gap-1">
          <CompactTextInput id={valueInputId} disabled onBlur={noop} value={props.value} />
          <CopyValueButton value={props.value} />
        </div>
      </div>
    </div>
  );
};

type NoEmailConfigurationProps = {
  actionOnClick: () => void;
};
const NoEmailConfiguration: VoidComponent<NoEmailConfigurationProps> = (props) => {
  const { t } = useTranslation();

  return (
    <EmptyState
      class="pt-8"
      title={t(($) => $.SETTINGS_EMAIL_CONFIGURATION_EMPTY_STATE_TITLE)}
      description={t(($) => $.SETTINGS_EMAIL_CONFIGURATION_EMPTY_STATE_DESCRIPTION)}
      illustration={{ primaryIcon: "mail" }}
      action={{
        variant: "light",
        icon: "plus",
        text: t(($) => $.SETTINGS_EMAIL_CONFIGURATION_EMPTY_STATE_ACTION_LABEL),
        onClick: props.actionOnClick,
      }}
    />
  );
};

const ProtectedEmailConfigurationPage = withPageProductFeatureCheck(
  withPagePermissionCheck(EmailConfigurationPage, PermissionService.canManageEmailConfiguration),
  "SENDING_DOMAIN",
);

export default ProtectedEmailConfigurationPage;
